import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Tag } from "@phosphor-icons/react";

interface PickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  subtitle?: string;
  icon?: React.ReactNode;
}

export const PickerModal = ({
  isOpen,
  onClose,
  title,
  children,
  subtitle = "Maak een keuze uit de onderstaande opties",
  icon
}: PickerModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Preserve all existing functionality - escape key handling
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Preserve all existing functionality - mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Use portal to render at document.body level, avoiding any parent
  // transforms/overflow that can break fixed positioning
  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex justify-center bg-black/60 backdrop-blur-xs animate-in fade-in duration-200 ${
        isMobile ? 'items-start pt-4' : 'items-center'
      }`}
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className={`w-full max-w-lg bg-muted shadow-2xl border border-muted-foreground/20 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 ease-out mx-4 flex flex-col ${
          isMobile ? 'rounded-2xl max-h-[85vh]' : 'rounded-2xl max-h-[70vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-muted-foreground/20 shrink-0">
          <div className="flex items-center gap-3">
            {/* Icon badge */}
            <div className="rounded-lg bg-moss-primary/10 p-2 shrink-0">
              {icon || <Tag className="h-5 w-5 text-moss-primary" weight="duotone" />}
            </div>
            {/* Title and subtitle */}
            <div className="min-w-0 flex-1">
              <h3 className="text-xl font-semibold text-neutral-primary leading-tight">{title}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
            </div>
          </div>
          {/* Close button */}
          <button
            onClick={onClose}
            className="shrink-0 ml-2 text-neutral-secondary-mid hover:text-neutral-primary transition-colors touch-manipulation p-1.5 rounded-lg hover:bg-neutral-primary/5"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content area — flex-1 + min-h-0 ensures it fills remaining space and scrolls */}
        <div className="flex-1 min-h-0 px-6 py-5 overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};